import {EditIcon} from "@chakra-ui/icons";
import {
  Button, Grid, GridItem,
  IconButton, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Td,
  Textarea,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, {useState} from "react";
import {useMutation, useQueryClient} from "react-query";

import {updateUser} from "../../shared/Api";
import {errorToast, successToast} from "../../shared/helper/ToastHelper";
import {Authority, UserStatus} from "../../shared/model/Constants";
import {UserDto} from "../../shared/model/Dto";
import {adminKeys} from "../../shared/QueryKeys";
import {displayUserRole, displayUserStatus} from "../../shared/Utils";
import DeleteUserIcon from "./DeleteUserIcon";

type UserSummaryItemProps = {
  user: UserDto;
  onUserDeleted: (username: string) => void;
};

const UserSummaryItem = (props: UserSummaryItemProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();

	const [usernameValue, setUsernameValue] = useState<string>(
    props.user.username !== null ? props.user.username : "");
	const [nameValue, setNameValue] = useState<string>(
    props.user.name !== null ? props.user.name : "");
	const [regionValue, setRegionValue] = useState<string>(
    props.user.region !== null ? props.user.region : "");
	const [emailValue, setEmailValue] = useState<string>(
    props.user.email !== null ? props.user.email : "");
	const [institutionValue, setInstitutionValue] = useState<string>(
    props.user.institution !== null ? props.user.institution : "");
	const [passwordValue, setPasswordValue] = useState<string>("");
	const [roleValue, setRoleValue] = useState<string>(
    props.user.authorities[0] === undefined ? "" : props.user.authorities[0]);
	const [statusValue, setStatusValue] = useState<string>(
    props.user.status === undefined ? "" : props.user.status);
	const [uGroupValue, setUGroupValue] = useState<string>(
    props.user.uGroup !== null ? props.user.uGroup : "");

  function handleDeleteClick(id: string) {
    props.onUserDeleted(id);
  }

  const updateUserMutation = useMutation(({username,
                                            usernameValue,
                                            nameValue,
                                            institutionValue,
                                            regionValue,
                                            emailValue,
                                            uGroupValue,
                                            passwordValue,
                                            roleValue,
                                            statusValue}: any) =>
      updateUser(username, usernameValue, nameValue, institutionValue,
      regionValue, emailValue, uGroupValue, passwordValue, roleValue, statusValue),
    {
      onSuccess: () => {
        props.user.username = usernameValue;
        props.user.name = nameValue;
        props.user.region = regionValue;
        props.user.institution = institutionValue;
        props.user.email = emailValue;
        props.user.uGroup = uGroupValue;
        props.user.status = UserStatus[statusValue  as keyof typeof UserStatus];
        if (roleValue === "")
          props.user.authorities = [];
        else
          props.user.authorities[0] = Authority[roleValue as keyof typeof Authority];
        successToast(toast, "Usuario actualizado");
        return queryClient.invalidateQueries(adminKeys.users());
      },
      onError: () => {
        errorToast(toast, "Error actualizando usuario");
      },
    });

  function handleSaveClick(id: string) {
    if (nameValue === null) setNameValue("");
    if (usernameValue === null) setUsernameValue("");
    if (institutionValue === null) setInstitutionValue("");
    if (regionValue === null) setRegionValue("");
    if (emailValue === null) setEmailValue("");
    if (passwordValue === null) setPasswordValue("");
    if (uGroupValue === null) setUGroupValue("");
    if (statusValue === null) setStatusValue("");
    if (props.user.authorities[0] === undefined || props.user.authorities[0] === null) setRoleValue("");

    updateUserMutation.mutate({username: id,
      usernameValue: usernameValue.trim(),
      nameValue: nameValue.trim(),
      institutionValue: institutionValue.trim(),
      regionValue: regionValue.trim(),
      emailValue: emailValue.trim(),
      uGroupValue: uGroupValue.trim(),
      passwordValue: passwordValue.trim(),
      roleValue: roleValue.trim(),
      statusValue: statusValue.trim()});
    onClose();
  }

  function setUserValues() {
    setUsernameValue(props.user.username !== null ? props.user.username : "");
    setNameValue( props.user.name !== null ? props.user.name : "");
    setRegionValue( props.user.region !== null ? props.user.region : "");
    setEmailValue( props.user.email !== null ? props.user.email : "");
    setInstitutionValue( props.user.institution !== null ? props.user.institution : "");
    setPasswordValue("");
    setRoleValue(props.user.authorities[0] === undefined ? "" : props.user.authorities[0]);
    setUGroupValue(props.user.uGroup !== null ? props.user.uGroup : "");
    setStatusValue(props.user.status !== null ? props.user.status : "");
  }

  return (
    <>
      <Tr key={props.user.username}>
        <Td>{props.user.username}</Td>
        <Td>{props.user.name}</Td>
        <Td>{props.user.institution}</Td>
        <Td>{props.user.region}</Td>
        <Td>{props.user.email}</Td>
        <Td>{props.user.uGroup}</Td>
        <Td>{displayUserRole(props.user.authorities[0])}</Td>
        <Td>{displayUserStatus(props.user.status)}</Td>
        <Td>
          <IconButton
            variant="outline"
            colorScheme="gray"
            aria-label="Editar Usuario"
            size="md"
            icon={<EditIcon/>}
            marginX="auto"
            onClick={()=>{
              setUserValues();
              onOpen();
            }} />
        </Td>
        <Td>
          <DeleteUserIcon
            onUserDeleted={() => handleDeleteClick(props.user.username)}
          />
        </Td>
      </Tr>

      <Modal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose} size={"3xl"}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar usuario</ModalHeader>
          <ModalBody>
            <Grid templateColumns='repeat(3, 1fr)' columnGap={5} rowGap={1}>
              <GridItem h='10'>Identificación</GridItem>
              <GridItem h='10'>Nombre</GridItem>
              <GridItem h='10'>Contraseña</GridItem>
              <GridItem>
                <Textarea placeholder={"Identificación"} value={usernameValue} minW="10em" resize="none"
                                  onChange={(event)=>setUsernameValue(event.target.value)}/>
              </GridItem>
              <GridItem>
                <Textarea placeholder={"Nombre"} value={nameValue} minW="10em" resize="none"
                          onChange={(event)=>setNameValue(event.target.value)}/>
              </GridItem>
              <GridItem>
                <Textarea placeholder={"Contraseña"} minW="10em" resize="none"
                                  onChange={(event)=>setPasswordValue(event.target.value)}/>
              </GridItem>
            </Grid>

            <Grid templateColumns='repeat(5, 1fr)' columnGap={5} rowGap={1}>
              <GridItem colSpan={2} h='10'>Correo</GridItem>
              <GridItem h='10'>Grupo</GridItem>
              <GridItem h='10'>Rol</GridItem>
              <GridItem h='10'>Estado</GridItem>
              <GridItem colSpan={2}>
                <Textarea placeholder={"Correo"} value={emailValue} minW="10em" resize="none"
                          onChange={(event)=>setEmailValue(event.target.value)}/>
              </GridItem>
              <GridItem>
                <Textarea placeholder={"Grupo"} value={uGroupValue} minW="10em" resize="none"
                          onChange={(event)=>setUGroupValue(event.target.value)}/>
              </GridItem>
              <GridItem>
                <RadioGroup onChange={setRoleValue} value={roleValue}>
                  <Stack direction='column'>
                    <Radio value={Authority.ROLE_ADMIN}>Admin</Radio>
                    <Radio value={Authority.ROLE_TEACHER}>Teacher</Radio>
                    <Radio value="">Sin rol</Radio>
                  </Stack>
                </RadioGroup>
              </GridItem>
              <GridItem>
                <RadioGroup onChange={setStatusValue} value={statusValue}>
                  <Stack direction='column'>
                    <Radio value={UserStatus.AVAILABLE}>Disponible</Radio>
                    <Radio value={UserStatus.RESERVED}>Reservada</Radio>
                    <Radio value={UserStatus.USED}>Usada</Radio>
                  </Stack>
                </RadioGroup>
              </GridItem>
            </Grid>

          </ModalBody>
          <ModalCloseButton />
          <ModalFooter>
            <Button
              marginX={10}
              colorScheme="teal"
              type="submit"
              onClick={() => handleSaveClick(props.user.username)}
            >
              Guardar
            </Button>
            <Button colorScheme="teal" type="submit" onClick={()=>{
              setUserValues();
              onClose();
            }} >
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UserSummaryItem;
